<template>
  <div class="ticket-etapas-fluxo">
    <div
      v-for="(etapaFluxo, index) in etapasTimeline"
      :key="`${ticketId}-${etapaFluxo.id}`"
      class="ticket-etapa-item"
      :class="{
        realizada: etapaFluxo.realizada,
        atual: etapaFluxo.atual,
        pendente: !etapaFluxo.realizada && !etapaFluxo.atual,
      }"
      :style="`--etapa-cor: ${etapaFluxo.cor}`"
    >
      <div class="ticket-etapa-stack">
        <div class="ticket-etapa-status">
          <v-icon x-small :color="etapaFluxo.statusCor">
            {{ etapaFluxo.statusIcone }}
          </v-icon>
          <span>{{ etapaFluxo.statusTexto }}</span>
        </div>

        <button
          type="button"
          class="ticket-etapa-pill"
          :title="tituloEtapa(etapaFluxo)"
          @click="$emit('selecionar-etapa', etapaFluxo)"
        >
          <div class="ticket-etapa-icone">
            <v-icon color="white" size="18">
              {{ etapaFluxo.icone }}
            </v-icon>
          </div>
          <div class="ticket-etapa-texto">
            <div class="ticket-etapa-titulo">
              <strong>{{ etapaFluxo.nome }}</strong>
              <span>
                <v-icon x-small>mdi-clock-outline</v-icon>
                {{ formatarSla(etapaFluxo) }}
              </span>
            </div>
            <div
              class="ticket-etapa-meta"
              :class="{ previsao: etapaFluxo.previsao }"
            >
              <span v-if="etapaFluxo.inicioExibicao">
                <v-icon x-small>mdi-play-circle-outline</v-icon>
                {{ formatarPrevisao(etapaFluxo.inicioExibicao) }}
              </span>
              <span v-if="etapaFluxo.fimExibicao">
                <v-icon x-small>mdi-flag-checkered</v-icon>
                {{ formatarPrevisao(etapaFluxo.fimExibicao) }}
              </span>
            </div>
          </div>
          <div
            class="ticket-etapa-colaborador"
            :class="{ 'sem-colaborador': !etapaFluxo.possuiColaborador }"
            :title="
              etapaFluxo.possuiColaborador
                ? 'Colaborador atribuido'
                : 'Sem responsavel atribuido'
            "
          >
            <v-icon size="16">
              {{
                etapaFluxo.possuiColaborador
                  ? "mdi-account-check-outline"
                  : "mdi-account-alert-outline"
              }}
            </v-icon>
          </div>
        </button>
      </div>

      <v-icon
        v-if="index < etapasTimeline.length - 1"
        class="ticket-etapa-seta"
        color="blue-grey lighten-1"
      >
        mdi-chevron-right
      </v-icon>
    </div>
  </div>
</template>

<script>
export default {
  name: "TicketFunerarioEtapasTimeline",

  props: {
    ticket: {
      type: Object,
      required: true,
    },
    status: {
      type: Object,
      default: null,
    },
    etapasFluxo: {
      type: Array,
      default: () => [],
    },
    somenteSemAtribuicao: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ticketId() {
      return this.ticket?.idAtendimento || this.ticket?.id || "ticket";
    },

    etapasTimeline() {
      if (!this.somenteSemAtribuicao) return this.etapasTimelineCompletas;
      return this.etapasTimelineCompletas.filter(
        (etapa) => !etapa.possuiColaborador,
      );
    },

    etapasTimelineCompletas() {
      const etapasBaseOriginal = this.etapasFluxo.length
        ? this.etapasFluxo
        : this.ticket?.etapas || [];
      const etapasBase = etapasBaseOriginal
        .filter((etapa) => this.etapaEmAndamento(etapa))
        .sort((a, b) => {
          const ordemA =
            a.ordem === null || a.ordem === undefined ? 999 : a.ordem;
          const ordemB =
            b.ordem === null || b.ordem === undefined ? 999 : b.ordem;
          if (ordemA !== ordemB) return ordemA - ordemB;
          return String(a.nome || "").localeCompare(String(b.nome || ""));
        });
      const etapaAtualId = Number(this.ticket?.etapa?.id);
      const etapasRegistradas = this.ticket?.etapas || [];

      let previsaoAnterior = this.parseDataHora(this.ticket?.dataAtendimento);

      return etapasBase.map((etapa) => {
        const etapaId = Number(etapa.id || etapa.etapaId);
        const registro = etapasRegistradas.find(
          (item) => Number(item.etapaId || item.id) === etapaId,
        );
        const atual = etapaAtualId === etapaId;
        const dtInicio = this.parseDataHora(registro?.dtInicio);
        const dtFim = this.parseDataHora(registro?.dtFim);
        const concluida = !!(dtInicio && dtFim);
        const previsaoInicio =
          this.parseDataHora(registro?.previsaoInicio) || previsaoAnterior;
        const previsaoFim =
          this.parseDataHora(registro?.previsaoFim) ||
          this.somarMinutos(previsaoInicio, etapa.sla);
        previsaoAnterior = previsaoFim || previsaoAnterior;

        return {
          ...etapa,
          id: etapaId,
          nome: etapa.nome || etapa.etapaNome || "Etapa",
          cor: etapa.cor || "#607D8B",
          icone: this.iconeEtapaTimeline(etapa),
          realizada: concluida,
          dtInicio,
          dtFim,
          previsaoInicio,
          previsaoFim,
          inicioExibicao: concluida ? dtInicio : previsaoInicio,
          fimExibicao: concluida ? dtFim : previsaoFim,
          previsao: !concluida,
          possuiColaborador: this.etapaPossuiColaborador(registro),
          statusTexto: this.statusTextoEtapa(concluida, atual),
          statusIcone: this.statusIconeEtapa(concluida, atual),
          statusCor: this.statusCorEtapa(concluida, atual),
          atual,
        };
      });
    },
  },

  methods: {
    etapaEmAndamento(etapa) {
      const tipo = String(etapa?.tipo || "").toLowerCase();
      return tipo === "em andamento" && etapa?.etapaSuporte !== true;
    },

    formatarSla(etapa) {
      if (!etapa) return "-";
      const sla = etapa.sla || etapa.tempoSla || etapa.slaMinutos;
      return sla ? `${sla} min` : "-";
    },

    etapaPossuiColaborador(registro) {
      if (!registro) return false;
      if (Array.isArray(registro.colaboradores)) {
        return registro.colaboradores.length > 0;
      }
      return !!(
        registro.colaborador ||
        registro.colaboradorId ||
        registro.idColaborador
      );
    },

    statusTextoEtapa(concluida, atual) {
      if (concluida) return "Concluido";
      if (atual) return "Atual";
      return "Previsão";
    },

    statusIconeEtapa(concluida, atual) {
      if (concluida) return "mdi-check-circle";
      if (atual) return "mdi-progress-clock";
      return "mdi-calendar-clock";
    },

    statusCorEtapa(concluida, atual) {
      if (concluida) return "green darken-1";
      if (atual) return "blue darken-1";
      return "blue-grey lighten-1";
    },

    tituloEtapa(etapa) {
      const linhas = [
        etapa?.nome || "Etapa",
        `SLA: ${this.formatarSla(etapa)}`,
      ];

      if (etapa?.realizada) {
        linhas.push(`Inicio real: ${this.formatarPrevisao(etapa.dtInicio)}`);
        linhas.push(`Fim real: ${this.formatarPrevisao(etapa.dtFim)}`);
      } else {
        linhas.push(`Inicio previsto: ${this.formatarPrevisao(etapa.previsaoInicio)}`);
        linhas.push(`Fim previsto: ${this.formatarPrevisao(etapa.previsaoFim)}`);
      }

      return linhas.join("\n");
    },

    parseDataHora(dataStr) {
      if (!dataStr) return null;
      if (dataStr instanceof Date) {
        return isNaN(dataStr.getTime()) ? null : dataStr;
      }

      const valor = String(dataStr).trim();
      const matchBR = valor.match(
        /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
      );
      if (matchBR) {
        const [, dia, mes, ano, hora = "00", minuto = "00", segundo = "00"] =
          matchBR;
        const dataBR = new Date(
          Number(ano),
          Number(mes) - 1,
          Number(dia),
          Number(hora),
          Number(minuto),
          Number(segundo),
        );
        return isNaN(dataBR.getTime()) ? null : dataBR;
      }

      const dataIso = new Date(valor);
      if (!isNaN(dataIso.getTime())) {
        return dataIso;
      }
      return null;
    },

    somarMinutos(data, minutos) {
      const inicio = this.parseDataHora(data);
      const quantidade = Number(minutos || 0);
      if (!inicio || !quantidade) return inicio;
      return new Date(inicio.getTime() + quantidade * 60000);
    },

    formatarPrevisao(data) {
      const valor = this.parseDataHora(data);
      if (!valor) return "--:--";
      const dia = String(valor.getDate()).padStart(2, "0");
      const mes = String(valor.getMonth() + 1).padStart(2, "0");
      const hora = String(valor.getHours()).padStart(2, "0");
      const minuto = String(valor.getMinutes()).padStart(2, "0");
      return `${dia}/${mes} ${hora}:${minuto}`;
    },

    iconeEtapaTimeline(etapa) {
      const iconesPorEtapa = {
        2: "mdi-file-document-plus-outline",
        3: "mdi-cross",
        4: "mdi-truck-outline",
        5: "mdi-account-group-outline",
        6: "mdi-car-outline",
        9: "mdi-shield-plus-outline",
      };
      const etapaId = Number(etapa?.id || etapa?.etapaId);
      if (iconesPorEtapa[etapaId]) return iconesPorEtapa[etapaId];

      const nome = String(etapa?.nome || etapa?.etapaNome || "").toLowerCase();
      if (nome.includes("acolhimento")) return "mdi-file-document-plus-outline";
      if (nome.includes("prepara")) return "mdi-cross";
      if (nome.includes("translado")) return "mdi-truck-outline";
      if (nome.includes("vel")) return "mdi-account-group-outline";
      if (nome.includes("transporte")) return "mdi-car-outline";
      if (nome.includes("somato")) return "mdi-shield-plus-outline";
      return "mdi-clipboard-check-outline";
    },
  },
};
</script>

<style scoped>
.ticket-etapas-fluxo {
  align-items: center;
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 6px 2px 8px;
}

@media (max-width: 768px) {
  .ticket-etapas-fluxo {
    flex-direction: column;
    align-items: stretch;
    overflow-x: visible;
  }
}

.ticket-etapa-item {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
}

@media (max-width: 768px) {
  .ticket-etapa-item {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
}

.ticket-etapa-stack {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 768px) {
  .ticket-etapa-stack {
    width: 100%;
  }
}

.ticket-etapa-status {
  align-items: center;
  color: #64748b;
  display: inline-flex;
  font-size: 10.5px;
  font-weight: 700;
  gap: 3px;
  justify-content: center;
  line-height: 1;
  padding-top: 2px;
  text-align: center;
  text-transform: uppercase;
}

@media (max-width: 768px) {
  .ticket-etapa-status {
    justify-content: flex-start;
    padding-left: 12px;
  }
}

.ticket-etapa-pill {
  align-items: center;
  background: #fff;
  border: 1px solid #dbe7f3;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  min-height: 46px;
  min-width: 178px;
  padding: 6px 10px 6px 7px;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
}

@media (max-width: 768px) {
  .ticket-etapa-pill {
    width: 100%;
    min-width: auto;
    border-radius: 12px;
  }
}

.ticket-etapa-pill:hover {
  background-color: #f8fbff;
  border-color: var(--etapa-cor, #607d8b);
  transform: translateY(-1px);
}

.ticket-etapa-pill:focus {
  outline: none;
}

.ticket-etapa-pill:focus-visible {
  border-color: var(--etapa-cor, #607d8b);
  box-shadow: 0 0 0 2px rgba(70, 130, 180, 0.18);
}

.ticket-etapa-icone {
  align-items: center;
  background-color: var(--etapa-cor, #607d8b);
  border-radius: 50%;
  display: flex;
  flex: 0 0 auto;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.ticket-etapa-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  min-width: 0;
}

@media (max-width: 768px) {
  .ticket-etapa-texto {
    align-items: flex-start;
    flex: 1;
  }
}

.ticket-etapa-titulo {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 4px 6px;
  justify-content: center;
  min-width: 0;
  text-align: center;
}

@media (max-width: 768px) {
  .ticket-etapa-titulo {
    justify-content: flex-start;
    text-align: left;
  }
}

.ticket-etapa-titulo strong {
  color: #0f172a;
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-etapa-titulo span {
  align-items: center;
  color: #64748b;
  display: inline-flex;
  font-size: 10.5px;
  font-weight: 500;
  gap: 3px;
  line-height: 1.2;
  white-space: nowrap;
}

.ticket-etapa-colaborador {
  align-items: center;
  background-color: #e6f4ea;
  border: 2px solid #68b684;
  border-radius: 50%;
  display: flex;
  flex: 0 0 auto;
  height: 28px;
  justify-content: center;
  margin-left: auto;
  width: 28px;
}

.ticket-etapa-colaborador .v-icon {
  color: #2f855a !important;
}

.ticket-etapa-colaborador.sem-colaborador {
  background-color: #fde8e8;
  border-color: #e53e3e;
}

.ticket-etapa-colaborador.sem-colaborador .v-icon {
  color: #c53030 !important;
}

.ticket-etapa-meta {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-wrap: nowrap;
  font-size: 10.5px;
  font-weight: 500;
  gap: 6px;
  justify-content: center;
  line-height: 1.2;
  text-align: center;
}

@media (max-width: 768px) {
  .ticket-etapa-meta {
    justify-content: flex-start;
    text-align: left;
    flex-wrap: wrap;
  }
}

.ticket-etapa-meta span {
  align-items: center;
  display: inline-flex;
  gap: 3px;
  white-space: nowrap;
}

.ticket-etapa-meta.previsao {
  color: #b7791f;
}

.ticket-etapa-meta.previsao .v-icon {
  color: #b7791f;
}

.ticket-etapa-item.pendente .ticket-etapa-pill {
  background-color: #f8fafc;
  border-color: #e5e7eb;
}

.ticket-etapa-item.atual .ticket-etapa-pill {
  background-color: #f8fbff;
  border-color: var(--etapa-cor, #607d8b);
  box-shadow: inset 0 0 0 1px var(--etapa-cor, #607d8b);
}

.ticket-etapa-seta {
  flex: 0 0 auto;
}

@media (max-width: 768px) {
  .ticket-etapa-seta {
    transform: rotate(90deg);
    margin: 4px 0;
  }
}
</style>
